import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { withFallback } from "./locales/with-fallback";
import { trackNamespace } from "./i18n-ns-tracker";

export const SUPPORTED_LANGUAGES = ["en", "fr", "de"];

const isSupported = (lng) => SUPPORTED_LANGUAGES.includes(lng);

// Namespaces the Layout renders on every page. They are fetched first and are
// always awaited before hydration.
export const CORE_NAMESPACES = ["common", "headerFooter"];

// Every page namespace. Anything outside CORE_NAMESPACES is fetched as its
// own JSON file (see the backend below), so no page waits for translations
// it does not render.
export const ALL_NAMESPACES = [
  "common",
  "homepage",
  "headerFooter",
  "servicesPage",
  "whyUsPage",
  "pricingPage",
  "blogPage",
  "resourcesPage",
  "mvpServicesPage",
  "appointmentsPage",
  "contactUsPage",
  "supportMaintenancePage",
  "aiSolutionsPage",
  "blockchainSolutionsPage",
  "iotDevelopmentPage",
  "thirdPartyIntegrationPage",
  "uiUxDesignPage",
  "qualityAssurancePage",
  "devOpsPage",
  "consultancyPage",
  "fullStackDevelopmentPage",
  "discoveryPage",
  "partnersPage",
  "privacyPolicyPage",
  "notFoundPage",
  "faqPage",
  "heystrangerCaseStudy",
  "dominionfundingCaseStudy",
  "cynoiaCaseStudy",
  "marketplacePage",
  "fintechMvpPage",
  "useCasesPage",
  "aboutUsPage",
  "neutureCaseStudy",
  "tradebrixCaseStudy",
  "qrinlyCaseStudy",
  "panamaseaCaseStudy",
  "mobileAppDevelopmentPage",
  "webPenetrationTestingPage",
  "gopilatesCaseStudy",
  "pitayaCaseStudy",
  "founderPage",
  "teamPage",
  "portfolioPage",
  "mediaPage",
  "saasDevelopmentPage",
  "scoperPage",
  "processRiskControlsPage",
  "pricingEstimator",
  "clientTestimonials",
];

const isBrowser = typeof window !== "undefined";

// react-i18next resolves every useTranslation() t() through the instance's
// getFixedT(), so wrapping it records each namespace a page actually renders.
// gatsby-ssr.js stamps that list into the HTML and the browser gates
// hydration on it — no hard-coded page→namespace map to maintain.
const originalGetFixedT = i18n.getFixedT.bind(i18n);
i18n.getFixedT = (...args) => {
  trackNamespace(args[1]);
  return originalGetFixedT(...args);
};

// Translations are served as plain JSON from /locales/<lng>/<ns>.json
// (mirrored into static/locales by gatsby-node.js onPreInit). fr/de locale
// files lack some English strings, so the generated overlay file supplies
// them; it is fetched once per session and merged per namespace.
let fallbackOverlayPromise = null;
const loadFallbackOverlay = (lng) => {
  if (lng === "en") return Promise.resolve(null);
  if (!fallbackOverlayPromise) {
    fallbackOverlayPromise = fetch(`/locales/${lng}/fallback.generated.json`)
      .then((res) => (res.ok ? res.json() : null))
      .catch(() => null);
  }
  return fallbackOverlayPromise;
};

// Minimal i18next backend. While a namespace loads, i18next keeps rendering
// raw keys and re-renders once the store emits "added", so a late namespace
// never blocks switching languages.
const localeBackend = {
  type: "backend",
  init(services) {
    this.store = services.resourceStore;
  },
  read(lng, ns, callback) {
    if (!isSupported(lng)) {
      callback(null, {});
      return;
    }
    Promise.all([
      fetch(`/locales/${lng}/${ns}.json`).then((res) =>
        res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status} for ${lng}/${ns}`))
      ),
      loadFallbackOverlay(lng),
    ])
      .then(([bundle, overlay]) => {
        const merged =
          overlay && overlay[ns] ? withFallback({ [ns]: bundle }, overlay)[ns] : bundle;
        if (bundle && !this.store.hasResourceBundle(lng, ns)) {
          this.store.addResourceBundle(lng, ns, merged, true, true, { silent: true });
        }
        callback(null, merged || {});
      })
      .catch((error) => callback(error, null));
  },
};

// Function to detect language from URL path
const detectLanguageFromPath = () => {
  if (typeof window !== "undefined") {
    const path = window.location.pathname;
    const pathSegments = path.split("/").filter((segment) => segment);

    // Check if first segment is a language code
    if (pathSegments.length > 0 && isSupported(pathSegments[0])) {
      return pathSegments[0];
    }
  }
  return null;
};

// Function to detect language from query parameter (fallback)
const detectLanguageFromQuery = () => {
  if (typeof window !== "undefined") {
    const urlParams = new URLSearchParams(window.location.search);
    const lang = urlParams.get("lang");
    if (lang && isSupported(lang)) {
      return lang;
    }
  }
  return null;
};

// Function to get current language
const getCurrentLanguage = () => {
  // SSR safety: return default language during SSR
  if (typeof window === "undefined") {
    return "en";
  }

  try {
    // First check URL path
    const pathLang = detectLanguageFromPath();
    if (pathLang) return pathLang;

    // Then check query parameter
    const queryLang = detectLanguageFromQuery();
    if (queryLang) return queryLang;

    // Then check localStorage
    const stored = localStorage.getItem("i18nextLng");
    if (stored && isSupported(stored)) {
      return stored;
    }

    // Finally check browser language
    const browserLang = navigator.language || navigator.userLanguage;
    if (browserLang) {
      const langCode = browserLang.split("-")[0];
      if (isSupported(langCode)) {
        return langCode;
      }
    }
  } catch (error) {
    // Silently fail and use fallback
    console.warn("Language detection failed during SSR:", error);
  }

  return "en";
};

// SSR attaches no backend: gatsby-ssr.js registers every language statically.
if (isBrowser) {
  i18n.use(localeBackend);
}

// Namespaces the server said this page renders (<html data-i18n-ns>, stamped
// by gatsby-ssr.js). Falls back to core-only when absent, e.g. dev without
// SSR — late namespaces still load and re-render through the store events.
const stampedNamespaces = () => {
  if (!isBrowser) return [];
  try {
    return (document.documentElement.getAttribute("data-i18n-ns") || "")
      .split(",")
      .filter((ns) => ns && ALL_NAMESPACES.includes(ns));
  } catch (error) {
    return [];
  }
};

// Only the core namespaces are required at init; everything else is pulled
// on demand (hydration gate below, or a component's own useTranslation call).
// Resolves once the current language and core are ready.
const initInstance = i18n.use(initReactI18next).init({
  lng: getCurrentLanguage(),
  // Each locale file already contains English text for its missing keys
  // (src/locales/with-fallback.js), so no fallback language is fetched.
  fallbackLng: false,

  interpolation: {
    escapeValue: false,
  },

  react: {
    useSuspense: false,
    bindI18n: 'languageChanged',
    bindI18nStore: 'added removed',
    transEmptyNodeValue: '',
    transSupportBasicHtmlNodes: true,
    transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p'],
  },

  returnEmptyString: false,
  returnNull: false,
  returnObjects: false,

  defaultNS: "common",
  ns: CORE_NAMESPACES,

  detection: {
    order: ["path", "querystring", "localStorage", "navigator", "htmlTag"],
    lookupQuerystring: "lang",
    caches: ["localStorage"],
  },

  debug: false,
});

// Hydration gate. Awaits exactly the namespaces this page rendered server-side
// (core + stamped), so the first client render matches the server HTML without
// downloading the other ~40 namespaces. The 3s cap trades a possible brief key
// flash for guaranteed interactivity if a request stalls.
const GATE_TIMEOUT_MS = 3000;
const loadBlockingNamespaces = async () => {
  const stamped = stampedNamespaces();
  const missing = [...new Set([...CORE_NAMESPACES, ...stamped])].filter(
    (ns) => !i18n.hasResourceBundle(i18n.language, ns)
  );
  if (missing.length) await i18n.loadNamespaces(missing);
};
export const i18nReady = Promise.race([
  initInstance.then(loadBlockingNamespaces),
  new Promise((resolve) => setTimeout(resolve, GATE_TIMEOUT_MS)),
]);

if (isBrowser) {
  // After hydration, quietly load every remaining namespace sequentially —
  // small requests, no burst — so client-side navigations never render
  // raw keys.
  i18nReady.then(() => {
    let chain = Promise.resolve();
    ALL_NAMESPACES
      .filter((ns) => !i18n.hasResourceBundle(i18n.language, ns))
      .forEach((ns) => {
        chain = chain.then(() => i18n.loadNamespaces(ns)).catch(() => {});
      });
    return chain;
  });
}

export default i18n;
